import { useState, useRef, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Invoice statuses matching backend schema (services/project-tracker/backend/schemas/invoices.py:12)
// V168 (2026-05-12): Added 'partially_paid' — automatic, set by record_payment when
// 0 < amount_paid < total_amount. Like 'paid', it cannot be set manually.
export type InvoiceStatus =
  | 'draft'
  | 'ready'
  | 'sent'
  | 'paid'
  | 'partially_paid'
  | 'cancelled';

interface InvoiceStatusOption {
  value: InvoiceStatus;
  label: string;
}

const invoiceStatuses: InvoiceStatusOption[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'ready', label: 'Ready' },
  { value: 'sent', label: 'Sent' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Phase 9.23c: More flexible status transitions
// Allow direct transitions without requiring step-through
// V168 (2026-05-12): Backend DOES enforce paid->other transitions (and now
// partially_paid is also payment-derived). Frontend transition map is a UX hint
// layered on top of that backend invariant.
// Phase 6 Fix: "paid" status is AUTOMATIC only (set by record_payment).
// V168: "partially_paid" is also AUTOMATIC only.
const ALLOWED_TRANSITIONS: Record<InvoiceStatus, InvoiceStatus[]> = {
  draft: ['ready', 'sent'],     // Phase 9.23c: Can skip 'ready' and go straight to 'sent'
  ready: ['draft', 'sent'],     // Can go back to draft or forward to sent
  sent: ['ready', 'draft'],     // Phase 9.23c: Can revert to ready or all the way to draft
  paid: ['sent'],               // Only backward to sent (if no payment exists)
  partially_paid: [],           // V168: Auto-derived; void payment to change
  cancelled: [],                // Terminal state (not used in normal workflow)
};

/**
 * Get allowed transitions for a given status
 * Phase 9.23c: More flexible - allows skipping intermediate states
 */
function getAllowedTransitions(currentStatus: InvoiceStatus | undefined): InvoiceStatus[] {
  if (!currentStatus) {
    return []; // No current status, no transitions
  }
  return ALLOWED_TRANSITIONS[currentStatus] || [];
}

interface InvoiceStatusSelectProps {
  value: InvoiceStatus | undefined;
  onChange: (value: InvoiceStatus) => void;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
  amountPaid?: number;  // ✅ NEW: Payment amount (blocks rollback from paid if > 0)
  /**
   * Render with the dropdown already open. Inline-edit flows (badge → select)
   * want this: without it the select mounts closed and "close without
   * selection cancels" can never fire — clicking away strands the row in
   * edit mode (pre-existing gap surfaced by 2026-06-03 staging QA).
   */
  defaultOpen?: boolean;

  // ── Theme 5b: doomed-affordance gating for Mark Ready / Mark Sent ──
  // The backend (routers/invoices.py:1455-1484) 400s a transition to 'ready' or
  // 'sent' when the invoice has NO line items OR when the line-item sum does not
  // match total_amount (0.01 tolerance). Offering those options and then 400ing
  // is a doomed affordance. Pass whatever gating data the page already has and
  // this component disables 'ready'/'sent' (with a tooltip) before the user can
  // pick them. All props optional — omit them and gating is simply off (legacy
  // behaviour). The list page only knows the total, so it can still gate the
  // common $0 case via invoiceTotal alone.
  /** Number of line items on the invoice. 0 → ready/sent are doomed. */
  lineItemCount?: number;
  /** invoice.total_amount — when 0, ready/sent are doomed (mirrors backend). */
  invoiceTotal?: number;
  /** Sum of (amount + tax_amount) across line items, for the mismatch check. */
  lineItemsSum?: number;
}

/**
 * Invoice Status dropdown selector
 *
 * Phase 6 Batch 5.3: Inline editing pattern
 * Phase 6: Invoice Status Rollback - One-step transitions
 * Phase 6 Fix: Payment constraints on status transitions
 * Phase 9.23c: More flexible transitions (draft->sent direct, sent->draft revert)
 * V168 (2026-05-12): 'paid' AND 'partially_paid' are payment-derived; dropdown is
 * disabled in those states with a tooltip pointing to "Void payment to change".
 *
 * Features:
 * - Auto-save on selection
 * - Cancel on close without selection
 * - Disabled state during mutation
 * - Shows allowed transitions (draft->ready/sent, ready->draft/sent, sent->ready/draft)
 * - Blocks rollback from 'paid' if payment exists (must delete payment first)
 * - Disables entirely on 'paid' and 'partially_paid' with explanatory tooltip
 */
export function InvoiceStatusSelect({
  value,
  onChange,
  onCancel,
  disabled = false,
  className,
  amountPaid,
  defaultOpen = false,
  lineItemCount,
  invoiceTotal,
  lineItemsSum,
}: InvoiceStatusSelectProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const valueChangedRef = useRef(false);

  // Theme 5b: compute WHY (if at all) a transition to 'ready'/'sent' would be
  // rejected by the backend, so we can disable those options + show the reason.
  // Returns undefined when the transition is allowed (or we lack the data to
  // judge — never block on missing data). Mirrors routers/invoices.py:1455-1484.
  const readySentBlockReason = useMemo<string | undefined>(() => {
    // No-items check: prefer the explicit count; fall back to total===0 (the
    // list page only knows the total). total===0 is the canonical doomed case.
    if (lineItemCount === 0) {
      return 'Add line items before marking this invoice ready or sent.';
    }
    if (lineItemCount === undefined && invoiceTotal === 0) {
      return 'Add line items before marking this invoice ready or sent.';
    }
    // Sum-mismatch check: only when we have BOTH the sum and the total.
    if (
      lineItemsSum !== undefined &&
      invoiceTotal !== undefined &&
      Math.abs(lineItemsSum - invoiceTotal) > 0.01
    ) {
      return 'Invoice total does not match the sum of line items. Fix the totals first.';
    }
    return undefined;
  }, [lineItemCount, invoiceTotal, lineItemsSum]);

  // V168: Disable dropdown entirely on payment-derived statuses. The user must
  // void the payment to change status — anything else is the bug we're fixing.
  const isPaymentDerived = value === 'paid' || value === 'partially_paid';
  const hasPayment = amountPaid !== undefined && amountPaid > 0;
  const lockedByPayment = isPaymentDerived && hasPayment;
  const effectiveDisabled = disabled || lockedByPayment;
  const lockTooltip = lockedByPayment
    ? 'Status is set automatically based on payment. Void payment to change status.'
    : undefined;

  // Filter statuses to only show one-step transitions
  // Include current status for label display (even though it can't be selected)
  // Payment constraint: Block rollback from 'paid' if payment exists
  const allowedStatuses = useMemo(() => {
    const allowedValues = getAllowedTransitions(value);

    // Filter based on payment constraints
    const filteredValues = allowedValues.filter((targetStatus) => {
      // Block rollback from 'paid' to 'sent' if payment exists
      if (value === 'paid' && targetStatus === 'sent') {
        // Only allow if no payment or amount is 0
        return !amountPaid || amountPaid === 0;
      }
      return true;
    });

    return invoiceStatuses.filter(
      (status) => filteredValues.includes(status.value) || status.value === value
    );
  }, [value, amountPaid]);

  const handleValueChange = (newValue: InvoiceStatus) => {
    // Theme 5b: defend the doomed transition even if a disabled item is somehow
    // activated (keyboard / a11y edge) — never fire a mutation the backend 400s.
    if ((newValue === 'ready' || newValue === 'sent') && readySentBlockReason) {
      return;
    }
    valueChangedRef.current = true;
    onChange(newValue);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    // If closing and no value was changed, call onCancel
    if (!open && !valueChangedRef.current && onCancel) {
      onCancel();
    }

    // Reset flag when opening
    if (open) {
      valueChangedRef.current = false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle Escape key to cancel editing even when dropdown is closed
    if (e.key === 'Escape' && !isOpen && onCancel) {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <Select
      value={value}
      onValueChange={handleValueChange}
      onOpenChange={handleOpenChange}
      open={isOpen}
      disabled={effectiveDisabled}
    >
      <SelectTrigger className={className} onKeyDown={handleKeyDown} title={lockTooltip}>
        <SelectValue placeholder="Select invoice status" />
      </SelectTrigger>
      <SelectContent>
        {allowedStatuses.map((status) => {
          // Theme 5b: disable Mark Ready / Mark Sent when the backend would 400.
          const isDoomed =
            (status.value === 'ready' || status.value === 'sent') &&
            !!readySentBlockReason;
          return (
            <SelectItem
              key={status.value}
              value={status.value}
              disabled={isDoomed}
              title={isDoomed ? readySentBlockReason : undefined}
            >
              {status.label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
